import ROUTES from "const/routes";
import { useI18n } from "providers/I18nProvider";
import { useCallback } from "react";
import { NavigateOptions, useLocation, useNavigate } from "react-router-dom";
import { SupportedLanguages } from "types";

type NavigationOptions = NavigateOptions & {
  preserveSearchParams?: boolean;
};

export function useNavigation(): (
  path: string,
  opts?: NavigationOptions,
) => void {
  const { language } = useI18n();
  const navigate = useNavigate();
  const { search, pathname: currentPathname } = useLocation();

  return useCallback(
    async (pathname, opts) => {
      const languages = Object.keys(SupportedLanguages).map((lang) =>
        lang.toLowerCase(),
      );
      const appPrefixes = ["admin", "employee"];
      const publicRoutes = [
        ROUTES.SIGN_IN,
        ROUTES.SIGN_IN_WITH_SAML,
        ROUTES.SIGN_UP,
        ROUTES.SIGN_UP_NEW_COMPANY_ADMIN,
        ROUTES.PASSWORD_FORGOTTEN,
        ROUTES.PASSWORD_RESET.split(":")[0],
        ROUTES.PASSWORD_SETUP,
        ROUTES.GOOGLE_REDIRECT,
        ROUTES.SAML_REDIRECT,
        ROUTES.EMAIL_VERIFICATION.split(":")[0],
      ];

      const [pathOnly, queryString] = pathname.split("?");
      const inputSearch = queryString ? `?${queryString}` : "";

      const firstPathComponent = pathOnly
        .split("/")
        .filter((component) => !!component)[0]
        ?.toLowerCase();

      const finalSearch =
        inputSearch || (opts?.preserveSearchParams ? search : "");

      if (!firstPathComponent || languages.includes(firstPathComponent)) {
        void navigate({ pathname: pathOnly, search: finalSearch }, opts);
        return;
      }

      const isPublicRoute = publicRoutes.some(
        (route) =>
          pathOnly.startsWith(route) || pathOnly.startsWith(`/${route}`),
      );

      if (isPublicRoute) {
        const publicPath = `/${language}/${pathOnly}`.replace(/\/+/g, "/");
        void navigate({ pathname: publicPath, search: inputSearch }, opts);
        return;
      }

      const currentPathSegments = currentPathname.split("/").filter(Boolean);
      const currentLangIndex = currentPathSegments.findIndex((seg) =>
        languages.includes(seg.toLowerCase()),
      );
      const afterLanguage = currentPathSegments.slice(currentLangIndex + 1);
      const currentAppPrefix = appPrefixes.includes(afterLanguage[0])
        ? afterLanguage[0]
        : null;

      let finalPath: string;
      if (currentAppPrefix) {
        finalPath = `/${language}/${currentAppPrefix}/${pathOnly}`.replace(
          /\/+/g,
          "/",
        );
      } else {
        finalPath = `/${language}/${pathOnly}`.replace(/\/+/g, "/");
      }

      void navigate({ pathname: finalPath, search: finalSearch }, opts);
    },
    [navigate, search, currentPathname, language],
  );
}
